import { useEffect, useState } from "react";
import api, { extractError } from "@/api/client";
import { X, Wallet, Send, Clock, CheckCircle, XCircle, AlertCircle, TrendingUp, Banknote, CreditCard, Building2, Save } from "lucide-react";

const STATUS_META = {
  pending:  { label: "قيد المراجعة", cls: "bg-yellow-100 text-yellow-700", icon: Clock },
  approved: { label: "معتمد",        cls: "bg-green-100 text-green-700",   icon: CheckCircle },
  rejected: { label: "مرفوض",        cls: "bg-red-100 text-red-700",       icon: XCircle },
};

const METHODS = [
  { value: "cash",   label: "نقداً",       icon: Banknote,   hint: "استلام يدوي من مقر الإدارة" },
  { value: "mypay",  label: "MyPay",       icon: CreditCard, hint: "تحويل إلى محفظة MyPay" },
  { value: "bank",   label: "تحويل مصرفي", icon: Building2,  hint: "تحويل إلى حساب مصرفي" },
];

/**
 * Driver-facing wallet cashout modal.
 * - Shows available balance (net_earnings − paid_out − pending)
 * - Method picker (cash / mypay / bank) with method-specific fields
 * - Optional "احفظ للمستقبل" to persist as default payout prefs
 * - Lists past requests with status pill
 */
export default function DriverCashoutModal({ onClose }) {
  const [data, setData] = useState(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [method, setMethod] = useState("cash");
  const [mypayPhone, setMypayPhone] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankAccount, setBankAccount] = useState("");
  const [bankHolder, setBankHolder] = useState("");
  const [saveDefault, setSaveDefault] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/driver/cashout");
      setData(data);
      const p = data.payout_prefs || {};
      setMethod(p.method || "cash");
      setMypayPhone(p.mypay_phone || "");
      setBankName(p.bank_name || "");
      setBankAccount(p.bank_account || "");
      setBankHolder(p.bank_holder || "");
    }
    catch (e) { setErr(extractError(e) || "تعذّر تحميل البيانات"); }
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    setErr("");
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setErr("أدخل مبلغاً صحيحاً"); return; }
    if (method === "mypay" && !mypayPhone.trim()) { setErr("أدخل رقم MyPay"); return; }
    if (method === "bank" && (!bankName.trim() || !bankAccount.trim())) { setErr("أدخل اسم المصرف ورقم الحساب"); return; }
    setBusy(true);
    try {
      await api.post("/driver/cashout", {
        amount: amt,
        note: note.trim() || undefined,
        payout_method: method,
        payout_details: {
          method,
          mypay_phone: method === "mypay" ? mypayPhone.trim() : null,
          bank_name:   method === "bank"  ? bankName.trim()   : null,
          bank_account: method === "bank" ? bankAccount.trim(): null,
          bank_holder: method === "bank"  ? bankHolder.trim() : null,
        },
        save_as_default: saveDefault,
      });
      setAmount(""); setNote("");
      await load();
    } catch (e) { setErr(extractError(e) || "تعذّر إرسال الطلب"); }
    finally { setBusy(false); }
  };

  const b = data?.balance;
  const M = METHODS.find((m) => m.value === method) || METHODS[0];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={onClose} data-testid="driver-cashout-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col" style={{maxHeight:"92vh"}}>
        <div className="flex justify-between items-center p-4 border-b border-[var(--border)] bg-gradient-to-l from-primary-brand to-[#1E3AB0] text-white">
          <div className="flex items-center gap-2"><Wallet size={18}/><h2 className="text-lg font-black" style={{fontFamily:'Cairo'}}>طلبات السحب</h2></div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center" data-testid="driver-cashout-close"><X size={16}/></button>
        </div>

        <div className="flex-1 overflow-auto">
          {b && (
            <div className="p-4">
              <div className="rounded-2xl bg-primary-light border border-primary-brand/20 p-4">
                <div className="text-xs text-primary-brand font-bold flex items-center gap-1"><TrendingUp size={12}/> الرصيد المتاح للسحب</div>
                <div className="mt-1 text-3xl font-black text-primary-brand" style={{fontFamily:'Cairo'}} data-testid="cashout-available">{b.available.toFixed(2)} د.ل</div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-center text-[11px]">
                  <div className="bg-white rounded-xl p-2"><div className="text-[var(--text-secondary)]">إجمالي الإيرادات</div><div className="font-black mt-0.5">{b.total_earned.toFixed(2)}</div></div>
                  <div className="bg-white rounded-xl p-2"><div className="text-[var(--text-secondary)]">مسحوب</div><div className="font-black mt-0.5">{b.paid_out.toFixed(2)}</div></div>
                  <div className="bg-white rounded-xl p-2"><div className="text-[var(--text-secondary)]">قيد المراجعة</div><div className="font-black mt-0.5">{b.pending.toFixed(2)}</div></div>
                </div>
              </div>
            </div>
          )}

          <div className="px-4 space-y-3">
            <div className="text-sm font-black" style={{fontFamily:'Cairo'}}>طلب سحب جديد</div>

            {/* Amount */}
            <div>
              <label className="text-xs text-[var(--text-secondary)] mb-1 block">المبلغ (د.ل)</label>
              <input
                type="number" inputMode="decimal" min={data?.min_amount || 10} step="0.5"
                value={amount} onChange={(e) => setAmount(e.target.value)}
                placeholder={`الحد الأدنى ${data?.min_amount || 10} د.ل`}
                className="w-full p-3 rounded-xl border border-[var(--border)] focus:border-primary-brand focus:outline-none text-lg font-black text-center"
                style={{fontFamily:'Cairo'}}
                data-testid="cashout-amount"
              />
            </div>

            {/* Method picker */}
            <div>
              <label className="text-xs text-[var(--text-secondary)] mb-2 block">طريقة الاستلام</label>
              <div className="grid grid-cols-3 gap-2">
                {METHODS.map((m) => (
                  <button
                    key={m.value}
                    onClick={() => setMethod(m.value)}
                    className={`p-3 rounded-2xl border-2 transition text-center ${method === m.value ? "bg-primary-light border-primary-brand text-primary-brand" : "bg-white border-[var(--border)] text-[var(--text-secondary)]"}`}
                    data-testid={`cashout-method-${m.value}`}
                  >
                    <m.icon size={18} className="mx-auto mb-1"/>
                    <div className="text-xs font-black" style={{fontFamily:'Cairo'}}>{m.label}</div>
                  </button>
                ))}
              </div>
              <div className="text-[11px] text-[var(--text-secondary)] mt-2 flex items-center gap-1">
                <M.icon size={12}/> {M.hint}
              </div>
            </div>

            {/* Method-specific fields */}
            {method === "mypay" && (
              <div>
                <label className="text-xs text-[var(--text-secondary)] mb-1 block">رقم MyPay</label>
                <input
                  value={mypayPhone} onChange={(e) => setMypayPhone(e.target.value)}
                  placeholder="09XXXXXXXX"
                  className="w-full p-3 rounded-xl border border-[var(--border)] focus:border-primary-brand focus:outline-none"
                  data-testid="cashout-mypay-phone"
                />
              </div>
            )}
            {method === "bank" && (
              <div className="space-y-2">
                <input
                  value={bankName} onChange={(e) => setBankName(e.target.value)}
                  placeholder="اسم المصرف"
                  className="w-full p-3 rounded-xl border border-[var(--border)] focus:border-primary-brand focus:outline-none"
                  data-testid="cashout-bank-name"
                />
                <input
                  value={bankAccount} onChange={(e) => setBankAccount(e.target.value)}
                  placeholder="رقم الحساب / IBAN"
                  className="w-full p-3 rounded-xl border border-[var(--border)] focus:border-primary-brand focus:outline-none font-mono"
                  data-testid="cashout-bank-account"
                />
                <input
                  value={bankHolder} onChange={(e) => setBankHolder(e.target.value)}
                  placeholder="اسم صاحب الحساب (اختياري)"
                  className="w-full p-3 rounded-xl border border-[var(--border)] focus:border-primary-brand focus:outline-none"
                  data-testid="cashout-bank-holder"
                />
              </div>
            )}

            {/* Note */}
            <div>
              <label className="text-xs text-[var(--text-secondary)] mb-1 block">ملاحظة (اختياري)</label>
              <input
                value={note} onChange={(e) => setNote(e.target.value)}
                maxLength={200}
                placeholder="أي تفاصيل إضافية..."
                className="w-full p-3 rounded-xl border border-[var(--border)] focus:border-primary-brand focus:outline-none text-sm"
                data-testid="cashout-note"
              />
            </div>

            {/* Save as default */}
            <label className="flex items-center gap-2 text-sm p-3 rounded-xl bg-[var(--surface-alt)] cursor-pointer" data-testid="cashout-save-default-label">
              <input
                type="checkbox" checked={saveDefault} onChange={(e) => setSaveDefault(e.target.checked)}
                className="w-4 h-4 accent-primary-brand"
                data-testid="cashout-save-default"
              />
              <Save size={14} className="text-primary-brand"/>
              <span className="flex-1 text-xs">احفظ هذه التفاصيل كطريقة استلام افتراضية للطلبات القادمة</span>
            </label>

            {err && (
              <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="cashout-err">
                <AlertCircle size={16} className="shrink-0 mt-0.5"/> {err}
              </div>
            )}
            <button onClick={submit} disabled={busy} className="w-full py-3 rounded-xl bg-primary-brand text-white font-black flex items-center justify-center gap-2 disabled:opacity-60" data-testid="cashout-submit">
              <Send size={16}/> {busy ? "جارٍ الإرسال..." : "إرسال طلب السحب"}
            </button>
          </div>

          {/* History */}
          <div className="p-4 mt-2">
            <div className="text-sm font-black mb-2" style={{fontFamily:'Cairo'}}>السجل</div>
            {!data ? (
              <div className="text-center text-sm text-[var(--text-secondary)] py-6">جارٍ التحميل...</div>
            ) : data.requests.length === 0 ? (
              <div className="text-center text-sm text-[var(--text-secondary)] py-6">لا يوجد طلبات سابقة</div>
            ) : (
              <div className="space-y-2">
                {data.requests.map((r) => {
                  const SM = STATUS_META[r.status] || STATUS_META.pending;
                  return (
                    <div key={r.id} className="bg-white rounded-2xl border border-[var(--border)] p-3" data-testid={`cashout-item-${r.id}`}>
                      <div className="flex justify-between items-start gap-2">
                        <div>
                          <div className="font-black text-lg" style={{fontFamily:'Cairo'}}>{r.amount.toFixed(2)} د.ل</div>
                          <div className="text-[11px] text-[var(--text-secondary)] mt-0.5">
                            {new Date(r.requested_at).toLocaleString("ar-LY", { dateStyle: "short", timeStyle: "short" })}
                          </div>
                          {r.payout_summary && (
                            <div className="text-[11px] text-primary-brand font-bold mt-1">
                              💳 {r.payout_summary}
                            </div>
                          )}
                        </div>
                        <span className={`inline-flex items-center gap-1 text-[10px] font-black px-2 py-1 rounded-full ${SM.cls}`}>
                          <SM.icon size={10}/> {SM.label}
                        </span>
                      </div>
                      {r.note && <div className="text-xs text-[var(--text-secondary)] mt-2">📝 {r.note}</div>}
                      {r.reject_reason && (
                        <div className="text-xs text-red-600 mt-2 bg-red-50 px-2 py-1 rounded">
                          سبب الرفض: {r.reject_reason}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
